import type { Turn } from '@/stores/useZinsserStore'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import { ZinsserThinkingBlock } from './ZinsserThinkingBlock'

interface ZinsserChatMessageProps {
  turn: Turn
  turnIndex: number        // Index in the turns array (kept for future use)
  isCurrentTurn?: boolean  // True if this is the turn currently being processed
  isProcessing?: boolean   // True if workflow is actively processing
}

export function ZinsserChatMessage({ turn, turnIndex: _turnIndex, isCurrentTurn = false, isProcessing = false }: ZinsserChatMessageProps) {
  const isUser = turn.role === 'user'

  // Don't show persisted node indicators for the current turn while processing
  // (they're shown in real-time in ZinsserChat.tsx instead)
  const showNodeIndicators = !isUser && !(isCurrentTurn && isProcessing)

  return (
    <div
      className={cn(
        'flex',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={cn(
          'max-w-[85%]',
          isUser
            ? 'rounded-lg px-4 py-3 bg-foreground text-background'
            : 'py-3'
        )}
      >
        {/* Collapsed thinking block for assistant messages (Gatekeeper thinking) */}
        {!isUser && turn.thinking && (
          <ZinsserThinkingBlock thinking={turn.thinking} isStreaming={false} />
        )}

        {/* Node indicators - ABOVE the response content */}
        {/* Zinsser - persisted indicator (shows if node ran, with optional thinking) */}
        {showNodeIndicators && turn.zinsserRan && (
          <div className="mb-2">
            {turn.zinsserThinking && (
              <ZinsserThinkingBlock thinking={turn.zinsserThinking} isStreaming={false} />
            )}
            <div className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
              <span>Zinsser</span>
            </div>
          </div>
        )}

        {/* CutClutter - persisted indicator */}
        {showNodeIndicators && turn.cutClutterRan && (
          <div className="mb-2">
            {turn.cutClutterThinking && (
              <ZinsserThinkingBlock thinking={turn.cutClutterThinking} isStreaming={false} />
            )}
            <div className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
              <span>Clutter</span>
            </div>
          </div>
        )}

        {/* StrengthenVerbs - persisted indicator */}
        {showNodeIndicators && turn.strengthenVerbsRan && (
          <div className="mb-2">
            {turn.strengthenVerbsThinking && (
              <ZinsserThinkingBlock thinking={turn.strengthenVerbsThinking} isStreaming={false} />
            )}
            <div className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
              <span>Verbs</span>
            </div>
          </div>
        )}

        {/* CheckLogic - persisted indicator */}
        {showNodeIndicators && turn.checkLogicRan && (
          <div className="mb-2">
            {turn.checkLogicThinking && (
              <ZinsserThinkingBlock thinking={turn.checkLogicThinking} isStreaming={false} />
            )}
            <div className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
              <span>Logic</span>
            </div>
          </div>
        )}

        {/* RhythmSound - persisted indicator (green = completed) */}
        {showNodeIndicators && turn.rhythmSoundRan && (
          <div className="mb-3">
            {turn.rhythmSoundThinking && (
              <ZinsserThinkingBlock thinking={turn.rhythmSoundThinking} isStreaming={false} />
            )}
            <div className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
              <span>Rhythm</span>
            </div>
          </div>
        )}

        {/* Responder - persisted indicator (green = completed) */}
        {showNodeIndicators && turn.responderRan && (
          <div className="mb-3">
            {turn.responderThinking && (
              <ZinsserThinkingBlock thinking={turn.responderThinking} isStreaming={false} />
            )}
            <div className="text-[10px] font-light uppercase tracking-[0.08em] text-green-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
              <span>Responder</span>
            </div>
          </div>
        )}

        {/* Response content */}
        {isUser ? (
          <div className="prose-chat-user text-sm max-w-none [&_*:last-child]:mb-0 [&_p]:mb-2">
            <ReactMarkdown>{turn.content.trim()}</ReactMarkdown>
          </div>
        ) : turn.isAnatomyInsight ? (
          <div className="border-l-2 border-amber-500/40 pl-3">
            {/* Anatomy insight header */}
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-block w-1.5 h-1.5 bg-amber-500/60 rounded-full" />
              <span className="text-[10px] font-light uppercase tracking-[0.08em] text-amber-600/80">
                Anatomy — {turn.anatomyScope}
              </span>
            </div>
            {/* Source text preview */}
            {turn.anatomySourceText && (
              <div className="text-[11px] text-muted-foreground/60 italic mb-2 leading-snug">
                "{turn.anatomySourceText}"
              </div>
            )}
            {/* Insight content — uses prose-chat for consistent typography */}
            <div className="prose-chat text-sm min-h-0 [&_*:last-child]:mb-0">
              <ReactMarkdown>{turn.content.trim()}</ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="prose-chat text-sm min-h-0 [&_*:last-child]:mb-0">
            <ReactMarkdown>{turn.content.trim()}</ReactMarkdown>
          </div>
        )}

        <div
          className={cn(
            'text-[10px] mt-1.5 font-light tracking-wide',
            isUser ? 'text-background/50' : 'text-muted-foreground/70'
          )}
        >
          {new Date(turn.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  )
}
